import React, { useState } from 'react';
import { Loader2, X, Upload, Replace, FileText, AlertTriangle, ArrowDownToLine, Check } from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { useImportTemplate, useSwapTemplate } from '../../services/documents';
import useEscapeToClose from './useEscapeToClose';

// Styled file picker — hides the clunky native input behind a hoverable drop-zone
// that shows the chosen filename.
export const FilePicker = ({ accept, file, onPick }) => (
  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-input bg-secondary/30 px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary">
    <Upload className="h-4 w-4 shrink-0" />
    <span className="max-w-[85%] truncate">{file ? file.name : 'Choose file'}</span>
    <input
      type="file"
      accept={accept}
      onChange={(e) => {
        onPick(e.target.files?.[0] || null);
        // Clear the input so re-picking the SAME file still fires onChange
        // (the browser suppresses it when the value hasn't changed).
        e.target.value = '';
      }}
      className="hidden"
    />
  </label>
);

const Overlay = ({ children, onClose }) => {
  useEscapeToClose(onClose);
  return (
    <div className="fixed inset-0 z-[1060] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card shadow-lg">{children}</div>
    </div>
  );
};

const Header = ({ title, onClose }) => (
  <div className="flex items-center justify-between border-b border-border px-4 py-3">
    <h3 className="text-base font-semibold text-foreground">{title}</h3>
    <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Close">
      <X className="h-4 w-4" />
    </button>
  </div>
);

const CancelButton = ({ onClose, disabled }) => (
  <button type="button" onClick={onClose} disabled={disabled} className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700">
    Cancel
  </button>
);

// Import a bundle as a NEW template in this environment.
export const ImportDocumentModal = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [parseError, setParseError] = useState(null);
  const importTpl = useImportTemplate();

  const onPick = async (f) => {
    setFile(f);
    setSummary(null);
    setParseError(null);
    if (!f) return;
    try {
      const bundle = JSON.parse(await f.text());
      const t = bundle?.template || {};
      setSummary({
        name: t.name || '(unnamed)',
        pageCount: t.pageCount ?? '—',
        fieldCount: Array.isArray(t.fieldPlacements) ? t.fieldPlacements.length : 0,
        process: t.processPurpose || 'Uncategorized',
      });
    } catch (e) {
      setParseError('That file is not a valid document export.');
    }
  };

  const submit = async () => {
    if (!file) {
      toast.error('Choose an export .json file.');
      return;
    }
    try {
      const res = await importTpl.mutateAsync(file);
      toast.success(res?.processMatched ? 'Imported into its process group.' : 'Imported.');
      onImported(res.template.documentId);
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Import failed.');
    }
  };

  return (
    <Overlay onClose={onClose}>
      <Header title="Import document" onClose={onClose} />
      <div className="space-y-3 p-4">
        <p className="text-sm text-muted-foreground">
          Upload a document export (<code className="text-xs">.json</code>).
        </p>
        <FilePicker accept="application/json,.json" file={file} onPick={onPick} />
        {parseError ? <p className="text-sm text-rose-600">{parseError}</p> : null}
        {summary ? (
          <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium text-foreground">
              <FileText className="h-4 w-4 text-muted-foreground" />
              {summary.name}
            </div>
            <div className="mt-1 text-muted-foreground">
              {summary.pageCount} page(s) · {summary.fieldCount} field(s) · {summary.process}
            </div>
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <CancelButton onClose={onClose} disabled={importTpl.isLoading} />
          <Button onClick={submit} disabled={importTpl.isLoading || !file || !!parseError} className="hover:bg-primary hover:opacity-100">
            {importTpl.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowDownToLine className="mr-2 h-4 w-4" />}
            Import
          </Button>
        </div>
      </div>
    </Overlay>
  );
};

// Swap an existing template's PDF (raw PDF: keep/clear fields; or an export bundle).
export const SwapDocumentModal = ({ document: doc, onClose, onSwapped }) => {
  const [file, setFile] = useState(null);
  const [keepFields, setKeepFields] = useState(true);
  const [shrink, setShrink] = useState(null); // { newPageCount, orphanedFields }
  const swap = useSwapTemplate();

  const isBundle = Boolean(file && (file.type === 'application/json' || file.name.toLowerCase().endsWith('.json')));

  const run = async (confirmShrink = false) => {
    if (!file) {
      toast.error('Choose a new PDF or an export .json.');
      return;
    }
    try {
      const res = await swap.mutateAsync({ documentId: doc.documentId, file, keepFields, confirmShrink });
      toast.success('Document replaced.');
      onSwapped?.(res.template.documentId);
      onClose();
    } catch (e) {
      const data = e?.response?.data;
      if (data?.warning === 'page_shrink') {
        setShrink({ newPageCount: data.newPageCount, orphanedFields: data.orphanedFields || [] });
        return;
      }
      toast.error(data?.msg || 'Swap failed.');
    }
  };

  const options = [
    { v: true, label: 'Keep fields', desc: 'Same spots & settings.' },
    { v: false, label: 'Start fresh', desc: 'Remove all fields.' },
  ];

  return (
    <Overlay onClose={onClose}>
      <Header title={`Replace — ${doc.name}`} onClose={onClose} />
      <div className="space-y-3 p-4">
        <p className="text-sm text-muted-foreground">
          Upload a new PDF or a Document Export (<code className="text-xs">.json</code>)
        </p>
        <FilePicker
          accept="application/pdf,application/json,.json"
          file={file}
          onPick={(f) => {
            setFile(f);
            setShrink(null);
          }}
        />

        {isBundle ? (
          <p className="rounded-md bg-secondary/40 p-3 text-xs text-muted-foreground">
            This export will replace the PDF <strong>and</strong> the fields.
          </p>
        ) : file ? (
          <div className="grid grid-cols-2 gap-2">
            {options.map((o) => {
              const active = keepFields === o.v;
              return (
                <button
                  key={String(o.v)}
                  type="button"
                  onClick={() => setKeepFields(o.v)}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-left transition-colors',
                    active
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-border hover:border-primary/40 hover:bg-accent',
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{o.label}</span>
                    {active ? <Check className="h-4 w-4 text-primary" /> : null}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{o.desc}</p>
                </button>
              );
            })}
          </div>
        ) : null}

        {shrink ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-500/40 dark:bg-amber-500/10">
            <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4" /> The new PDF has only {shrink.newPageCount} page(s).
            </div>
            <p className="mt-1 text-amber-800/90 dark:text-amber-200/80">
              {shrink.orphanedFields.length} field(s) sit on pages that no longer exist and will be dropped:{' '}
              {shrink.orphanedFields.map((f) => f.label || f.fieldId).join(', ')}.
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <CancelButton onClose={onClose} disabled={swap.isLoading} />
          {shrink ? (
            <Button onClick={() => run(true)} disabled={swap.isLoading} className="bg-amber-600 text-white hover:bg-amber-600 hover:opacity-90">
              {swap.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Replace anyway (drop {shrink.orphanedFields.length})
            </Button>
          ) : (
            <Button onClick={() => run(false)} disabled={swap.isLoading || !file} className="hover:bg-primary hover:opacity-100">
              {swap.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Replace className="mr-2 h-4 w-4" />}
              Replace
            </Button>
          )}
        </div>
      </div>
    </Overlay>
  );
};
